import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { CircleDashed, Database, FlaskConical, Unplug } from 'lucide-react';
import * as React from 'react';

import { useZone } from '@/design';

/**
 * What says a screen is invented — the onboarding study's two marks, in the brand palette (the
 * two-portals design, §5 and §8): the **status strip** over a whole mock screen, and a **source
 * badge** on each of its panels. Both stay on screen with the guide closed, because the mode the
 * guide is closed in is the one a screenshot is taken in, and an invented screen unmarked in a
 * screenshot is the one somebody takes for the product.
 *
 * Mock is the guideline Yellow, dashed at its darkened edge — the status chip's `mock` tone — and
 * nothing else on either portal is drawn that way. A live screen carries none of this.
 */

export type MockSource = 'fixture' | 'no-endpoint' | 'not-built';

const SOURCE_LABEL: Record<MockSource, string> = {
  fixture: 'Invented data',
  'no-endpoint': 'No endpoint',
  'not-built': 'Not built'
};

const SOURCE_NOTE: Record<MockSource, string> = {
  fixture:
    'Every value here is the demo’s own fixture, the same invented cast on every screen. None of it is real.',
  'no-endpoint':
    'Nothing stores this yet. What the screen does happens in this page’s memory and is gone on a reload or "Reset demo".',
  'not-built': 'Not worth faking in the demo: the control is drawn so the screen is whole, and does nothing.'
};

const SOURCE_ICON: Record<MockSource, React.ComponentType<{ className?: string }>> = {
  fixture: Database,
  'no-endpoint': Unplug,
  'not-built': CircleDashed
};

const MOCK_FILL =
  'border-[var(--veritant-yellow-edge)] bg-[color-mix(in_srgb,var(--color-yellow)_30%,white)] text-[var(--color-midnight-blue)]';

/**
 * One panel's source, with its definition a keyboard can reach — a button, for the study's
 * reason: the tooltip is the only place the badge's words are defined.
 */
export function SourceBadge({ source, className }: { source: MockSource; className?: string }) {
  const Icon = SOURCE_ICON[source];
  const dense = useZone() === 'team';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-demo-badge={source}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full border border-dashed font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring',
            dense ? 'h-5 px-2 text-[0.6875rem]' : 'h-6 px-2.5 text-xs',
            source === 'not-built'
              ? 'border-[var(--veritant-yellow-edge)] bg-white text-[var(--color-midnight-blue)]'
              : MOCK_FILL,
            className
          )}
        >
          <Icon className="size-3" aria-hidden />
          {SOURCE_LABEL[source]}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">{SOURCE_NOTE[source]}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The strip over a mock screen: that it is a mock-up, and what the product would need for it to be
 * real. A `note`, outside the screen's own card, so no copy of the screen's can be read as it.
 */
export function DemoStrip({ missing, className }: { missing: React.ReactNode; className?: string }) {
  const team = useZone() === 'team';
  return (
    <div
      role="note"
      aria-label="Mock screen"
      data-demo-strip=""
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border border-dashed',
        MOCK_FILL,
        team ? 'mx-6 mt-4 rounded-md px-3 py-1.5 text-[0.8125rem]' : 'rounded-lg px-4 py-2.5 text-sm',
        className
      )}
    >
      <FlaskConical className="size-4 shrink-0" aria-hidden />
      <span className="font-medium">Mock screen — the demo’s invented data</span>
      <span className="min-w-0 basis-full pl-7 text-[0.8125rem] sm:basis-auto sm:pl-0">{missing}</span>
    </div>
  );
}

/**
 * A control drawn so a screen is whole, and inert: disabled to the pointer and the keyboard alike,
 * and tagged — never a button that looks live and silently does nothing.
 */
export function NotBuilt({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const dense = useZone() === 'team';
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <button
        type="button"
        disabled
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-dashed border-[var(--veritant-royal-55)] bg-white text-muted-foreground',
          dense ? 'h-8 px-3 text-[0.8125rem]' : 'h-11 px-4 text-[0.9375rem]',
          'cursor-not-allowed'
        )}
      >
        {children}
      </button>
      <SourceBadge source="not-built" />
    </span>
  );
}

/**
 * A mock panel: a card of its own with its title and its source badge in the heading line. A mock
 * that extends a live screen — Help's contact block, the in-consideration thresholds on the
 * concern criteria — is one of these inside the live screen, never restyled to pass as live.
 */
export function MockPanel({
  title,
  source = 'fixture',
  description,
  actions,
  headingLevel = 2,
  id,
  className,
  children
}: {
  title: string;
  source?: MockSource;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  headingLevel?: 2 | 3;
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const team = useZone() === 'team';
  const Heading = `h${headingLevel}` as const;
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      data-demo-panel=""
      className={cn(
        'min-w-0 rounded-lg border border-dashed border-[var(--veritant-yellow-edge)] bg-white',
        team ? 'p-4' : 'p-6',
        className
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="grid min-w-0 gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Heading
              id={id ? `${id}-title` : undefined}
              className={cn(
                'font-medium text-[var(--color-royal-blue)]',
                team ? 'text-[0.9375rem]' : 'text-lg'
              )}
            >
              {title}
            </Heading>
            <SourceBadge source={source} />
          </div>
          {description && (
            <p className={cn('text-muted-foreground', team ? 'text-[0.8125rem]' : 'text-[0.9375rem]')}>
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className={cn(team ? 'mt-3' : 'mt-4')}>{children}</div>
    </section>
  );
}

/** A native select in the zone's density — the demo's tables edit inline, where a popover per cell
 *  would bury the row it edits. */
export const DemoSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function DemoSelect({ className, ...props }, ref) {
  const dense = useZone() === 'team';
  return (
    <select
      ref={ref}
      {...props}
      className={cn(
        'rounded-md border border-input bg-white text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
        dense ? 'h-7 px-1.5 text-[0.8125rem]' : 'h-11 px-3 text-[0.9375rem]',
        className
      )}
    />
  );
});
